import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ParsedAuction } from './AuctionContext';

export interface BuyerInfo {
  name: string;
  surname: string;
  email: string;
  phone: string;
  street: string;
  aptNumber?: string;
  city: string;
  zip: string;
}

export interface BillingInfo {
  name?: string;
  surname?: string;
  street: string;
  aptNumber?: string;
  city: string;
  zip: string;
}

interface CartContextType {
  cartProduct: ParsedAuction | null;
  setCartProduct: (product: ParsedAuction | null) => void;
  clearCart: () => void;
  checkoutStep: number;
  setCheckoutStep: (step: number) => void;
  buyerInfo: BuyerInfo | null;
  setBuyerInfo: (info: BuyerInfo) => void;
  billingInfo: BillingInfo | null;
  setBillingInfo: (info: BillingInfo | null) => void;
  sameAddress: boolean;
  setSameAddress: (same: boolean) => void;
  orderId: number | null;
  setOrderId: (id: number | null) => void;
  paymentIntentClientSecret: string | null;
  setPaymentIntentClientSecret: (secret: string | null) => void;
  checkoutDeadline: number | null;
  startCheckout: () => void;
  completeCheckout: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

const CHECKOUT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const CHECKOUT_STORAGE_KEY = 'shoppingCartCheckout';

interface PersistedCheckoutState {
  checkoutStep: number;
  buyerInfo: BuyerInfo | null;
  billingInfo: BillingInfo | null;
  sameAddress: boolean;
  orderId: number | null;
  checkoutDeadline: number | null;
}

const readPersistedCheckoutState = (): PersistedCheckoutState => {
  try {
    const savedCheckout = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!savedCheckout) {
      return {
        checkoutStep: 0,
        buyerInfo: null,
        billingInfo: null,
        sameAddress: true,
        orderId: null,
        checkoutDeadline: null,
      };
    }

    return JSON.parse(savedCheckout);
  } catch {
    localStorage.removeItem(CHECKOUT_STORAGE_KEY);
    return {
      checkoutStep: 0,
      buyerInfo: null,
      billingInfo: null,
      sameAddress: true,
      orderId: null,
      checkoutDeadline: null,
    };
  }
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [persistedCheckoutState] = useState(readPersistedCheckoutState);
  const [cartProduct, setCartProductState] = useState<ParsedAuction | null>(() => {
    try {
      const savedCart = localStorage.getItem('shoppingCart');
      return savedCart ? JSON.parse(savedCart) : null;
    } catch {
      localStorage.removeItem('shoppingCart');
      return null;
    }
  });

  const [checkoutStep, setCheckoutStep] = useState(persistedCheckoutState.checkoutStep);
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo | null>(persistedCheckoutState.buyerInfo);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(persistedCheckoutState.billingInfo);
  const [sameAddress, setSameAddress] = useState(persistedCheckoutState.sameAddress);
  const [orderId, setOrderId] = useState<number | null>(persistedCheckoutState.orderId);
  const [paymentIntentClientSecret, setPaymentIntentClientSecret] = useState<string | null>(null);
  const [checkoutDeadline, setCheckoutDeadline] = useState<number | null>(
    persistedCheckoutState.checkoutDeadline
  );

  useEffect(() => {
    if (cartProduct) {
      localStorage.setItem('shoppingCart', JSON.stringify(cartProduct));
    } else {
      localStorage.removeItem('shoppingCart');
    }
  }, [cartProduct]);

  useEffect(() => {
    const isEmptyCheckout =
      checkoutStep === 0 &&
      !buyerInfo &&
      !billingInfo &&
      sameAddress &&
      !orderId &&
      !checkoutDeadline;

    if (isEmptyCheckout) {
      localStorage.removeItem(CHECKOUT_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      CHECKOUT_STORAGE_KEY,
      JSON.stringify({
        checkoutStep,
        buyerInfo,
        billingInfo,
        sameAddress,
        orderId,
        checkoutDeadline,
      })
    );
  }, [billingInfo, buyerInfo, checkoutDeadline, checkoutStep, orderId, sameAddress]);

  const resetCheckoutState = useCallback(() => {
    setCheckoutStep(0);
    setBuyerInfo(null);
    setBillingInfo(null);
    setSameAddress(true);
    setOrderId(null);
    setPaymentIntentClientSecret(null);
    setCheckoutDeadline(null);
  }, []);

  const setCartProduct = useCallback((product: ParsedAuction | null) => {
    const currentProductKey = cartProduct?.auctionId ?? cartProduct?.title ?? null;
    const nextProductKey = product?.auctionId ?? product?.title ?? null;
    const hasAuctionChanged =
      currentProductKey !== nextProductKey;

    setCartProductState(product);
    if (hasAuctionChanged) {
      resetCheckoutState();
    }
  }, [cartProduct?.auctionId, cartProduct?.title, resetCheckoutState]);

  const startCheckout = useCallback(() => {
    setCheckoutDeadline((currentDeadline) => {
      if (currentDeadline && currentDeadline > Date.now()) {
        return currentDeadline;
      }

      return Date.now() + CHECKOUT_TIMEOUT_MS;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartProductState(null);
    resetCheckoutState();
  }, [resetCheckoutState]);

  const completeCheckout = useCallback(() => {
    setCheckoutStep(3);
    setCheckoutDeadline(null);
  }, []);

  return (
    <CartContext.Provider value={{
      cartProduct,
      setCartProduct,
      clearCart,
      checkoutStep,
      setCheckoutStep,
      buyerInfo,
      setBuyerInfo,
      billingInfo,
      setBillingInfo,
      sameAddress,
      setSameAddress,
      orderId,
      setOrderId,
      paymentIntentClientSecret,
      setPaymentIntentClientSecret,
      checkoutDeadline,
      startCheckout,
      completeCheckout,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
